import React, { useState } from 'react';
import { Trophy, TrendingUp, Play, CheckCircle2, XCircle } from 'lucide-react';
import { useDemoLang } from './demoLang';

const TESTS = [
  {
    id: 'sat', name: 'SAT', color: '#0F4C4C',
    fr: { age: '15-18 ans', desc: "Test américain pour l'admission universitaire (maths, lecture, écriture). Reconnu dans le monde entier." },
    en: { age: 'Ages 15-18', desc: 'US college-admission test (math, reading, writing). Accepted worldwide.' },
    sample: {
      fr: { q: "Si 3x + 5 = 20, quelle est la valeur de x ?", options: ["3", "5", "7", "15"], correct: 1 },
      en: { q: "If 3x + 5 = 20, what is the value of x?", options: ["3", "5", "7", "15"], correct: 1 },
    },
  },
  {
    id: 'toefl', name: 'TOEFL', color: '#c47a2a',
    fr: { age: '13-18 ans', desc: "Anglais pour universités anglophones. Écoute, lecture, expression orale, rédaction." },
    en: { age: 'Ages 13-18', desc: 'English for study in anglophone universities. Listening, reading, speaking, writing.' },
    sample: {
      fr: { q: "Choisissez le synonyme de 'meticulous':", options: ["Careless", "Careful", "Rapid", "Loud"], correct: 1 },
      en: { q: "Choose the synonym of 'meticulous':", options: ["Careless", "Careful", "Rapid", "Loud"], correct: 1 },
    },
  },
  {
    id: 'testdaf', name: 'TestDaF', color: '#8b1f2f',
    fr: { age: '15+ ans', desc: "Allemand niveau universitaire pour étudier en Allemagne. Standardisé à travers le monde." },
    en: { age: 'Ages 15+', desc: 'University-level German for studying in Germany. Globally standardised.' },
    sample: {
      fr: { q: "Wählen Sie die korrekte Form: 'Ich ___ nach Berlin.'", options: ["fahren", "fährt", "fahre", "fuhr"], correct: 2 },
      en: { q: "Pick the correct form: 'Ich ___ nach Berlin.'", options: ["fahren", "fährt", "fahre", "fuhr"], correct: 2 },
    },
  },
  {
    id: 'gmat', name: 'GMAT', color: '#5b5b8f',
    fr: { age: '17+ ans', desc: "Test pour les écoles de commerce (MBA). Raisonnement quantitatif, verbal et intégré." },
    en: { age: 'Ages 17+', desc: 'Business-school (MBA) admissions test. Quantitative, verbal and integrated reasoning.' },
    sample: {
      fr: { q: "Une entreprise gagne 20% de plus chaque année. Après 3 ans, son revenu a été multiplié par…", options: ["1.6", "1.728", "2.0", "2.4"], correct: 1 },
      en: { q: "A company grows revenue by 20% each year. After 3 years, revenue has multiplied by…", options: ["1.6", "1.728", "2.0", "2.4"], correct: 1 },
    },
  },
];

const AgeStart = ({ t }) => {
  const [age, setAge] = useState(10);
  return (
    <section className="rounded-3xl bg-gradient-to-br from-navy to-teal p-5 text-white shadow-squishyLg" data-testid="age-tracker">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4 text-gold" />
        <span className="text-gold font-heading font-bold text-[10px] uppercase tracking-wider">
          {t("Bâtir les fondations", "Building the foundation")}
        </span>
      </div>
      <div className="font-heading text-xl font-bold" data-testid="age-headline">
        {t(`Bâtir sa préparation dès l'âge de ${age} ans`, `Building your foundation from age ${age}`)}
      </div>
      <div className="mt-3">
        <input type="range" min={7} max={16} value={age} onChange={(e) => setAge(Number(e.target.value))}
          className="w-full accent-gold" data-testid="age-range" />
        <div className="flex justify-between text-white/60 text-[10px] mt-1"><span>7</span><span>10</span><span>13</span><span>16</span></div>
      </div>
      <div className="mt-3 rounded-2xl bg-gold/20 border border-gold/40 p-3 text-sm">
        📈 {t("Les enfants qui commencent à 10 ans obtiennent en moyenne 23% de meilleurs scores.",
              "Children who start at age 10 score 23% higher on average.")}
      </div>
    </section>
  );
};

const SampleQuiz = ({ test, onClose, t, lang }) => {
  const [picked, setPicked] = useState(null);
  const s = test.sample[lang] || test.sample.en;
  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="test-sample-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 text-white rounded-t-3xl" style={{ background: test.color }}>
          <div className="text-white/80 font-heading font-bold text-[10px] uppercase tracking-wider">{test.name} · {t("Question exemple", "Sample question")}</div>
          <h3 className="font-heading text-xl font-bold mt-1">{s.q}</h3>
        </div>
        <div className="p-4 space-y-2">
          {s.options.map((opt, i) => {
            const isPicked = picked === i;
            const isCorrect = i === s.correct;
            const state = picked === null ? 'idle' : isCorrect ? 'correct' : (isPicked ? 'wrong' : 'idle');
            return (
              <button key={i} onClick={() => setPicked(i)} disabled={picked !== null}
                className={`w-full text-left p-3 rounded-2xl border-2 flex items-center gap-2 font-heading font-bold text-sm transition-colors ${state === 'correct' ? 'bg-teal/15 border-teal text-navy' : state === 'wrong' ? 'bg-red-50 border-red-300 text-navy' : 'bg-white border-cream text-navy hover:border-teal/60'}`}
                data-testid={`test-option-${i}`}>
                {state === 'correct' && <CheckCircle2 className="w-4 h-4 text-teal" />}
                {state === 'wrong' && <XCircle className="w-4 h-4 text-red-500" />}
                <span>{opt}</span>
              </button>
            );
          })}
          {picked !== null && (
            <div className="rounded-2xl bg-cream p-3 text-sm text-navy" data-testid="test-feedback">
              {picked === s.correct
                ? t("Bravo ! Tu es sur la bonne voie.", "Great! You're on track.")
                : t("Pas cette fois. Un tuteur t'expliquera pas à pas.", "Not this time. A tutor will walk you through it.")}
            </div>
          )}
          <button onClick={onClose} className="mt-2 w-full h-12 rounded-full bg-navy text-white font-heading font-bold" data-testid="test-close">
            {t("Fermer", "Close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TestPrep() {
  const { lang, t } = useDemoLang();
  const [openTest, setOpenTest] = useState(null);

  return (
    <div className="space-y-5" data-testid="testprep-page">
      <AgeStart t={t} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="test-grid">
        {TESTS.map((tt) => {
          const info = lang === 'fr' ? tt.fr : tt.en;
          return (
            <div key={tt.id} className="rounded-3xl overflow-hidden bg-white border-2 border-cream shadow-squishy" data-testid={`test-card-${tt.id}`}>
              <div className="p-4 text-white" style={{ background: tt.color }}>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center"><Trophy className="w-5 h-5 text-white" /></div>
                  <div>
                    <div className="font-heading text-lg font-bold">{tt.name}</div>
                    <div className="text-white/80 text-[10px] font-bold uppercase tracking-wider">{info.age}</div>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-navy/80 text-sm">{info.desc}</p>
                <button onClick={() => setOpenTest(tt)}
                  className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-gold text-navy font-heading font-bold text-sm shadow-gold hover:scale-105 transition-transform"
                  data-testid={`start-prep-${tt.id}`}>
                  <Play className="w-4 h-4" /> {t("Commencer la prépa", "Start Prep")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {openTest && <SampleQuiz test={openTest} onClose={() => setOpenTest(null)} t={t} lang={lang} />}
    </div>
  );
}
